import { MedplumClient } from "@medplum/core";
import { serviceTypeMap } from "./EligibilityDataMap";
import { title } from "process";

const codingSystemMap = {
  patient: "http://hl7.org/fhir/us/carin-bb/CodeSystem/C4BBIdentifierType",
  pverifyPayer: "https://pverify.io/eligibility-all-payer-list-api/",
  availityPayer: 'https://api.availity.com/availity/v1/availity-payer-list',
  provider: "http://hl7.org/fhir/sid/us-npi",
  pverify: "https://api.pverify.com/API/EligibilitySummary",
  availity: 'https://api.availity.com/availity/v1/coverages',
  benefitCategory: "http://terminology.hl7.org/CodeSystem/benefit-network",
  benefitUnit: "http://terminology.hl7.org/CodeSystem/benefit-unit",
  actCode: "http://terminology.hl7.org/CodeSystem/v3-ActCode",
};

const getPatientList = async (medplum: MedplumClient) => {
  const res = await medplum.searchResources("Patient", "_count=500");
  const patientData = res.map((patient: any) => {
    return {
      ...patient,
      value: patient.id,
      label: getPatientName(patient.name),
      name: getPatientName(patient.name),
      lastName: patient.name[0].family,
    };
  });

  return patientData;
};

const updatePatient = async (medplum: MedplumClient, patient: any) => {
  const oldPatient = await medplum.readResource("Patient", patient.id);
  const res = await medplum.updateResource({
    ...oldPatient,
    birthDate: patient.birthDate.toISOString().split("T")[0],
  });

  return res;
};

const updateProvider = async (medplum: MedplumClient, provider: any) => {
  const oldProvider = await medplum.readResource("Organization", provider.id);
  const res = await medplum.updateResource({
    ...oldProvider,
    identifier: [
      ...(oldProvider.identifier as Array<any>),
      {
        system: codingSystemMap["provider"],
        value: provider.npi,
      },
    ],
  });

  return res;
};

const getRelatedPersonList = async (medplum: MedplumClient, dependent = "") => {
  const res = await medplum.searchResources(
    "RelatedPerson",
    `patient=${dependent}, _count=500`
  );
  const personData = res.map((person: any) => {
    return {
      ...person,
      value: person.id,
      label: getPatientName(person.name),
      lastName: person.name[0].family,
    };
  });
  return personData;
};

const getPayerList = async (medplum: MedplumClient, serviceType: 'pverify' | 'availity') => {
  const res = (await medplum.searchResources(
    "Organization",
    "_count=500"
  )) as any;
  const payerData = res.map((payer: any) => {
    const code = payer.identifier
      ? payer.identifier.find(
          (id: any) => id.system == codingSystemMap[`${serviceType}Payer`]
        )
      : null;
    return {
      code: code?.value,
      value: payer.id,
      label: `${payer.name} (${code?.value})`,
    };
  });
  // filter out empty codes
  return payerData.filter((payer: any) => payer.code);
};

const getProviderList = async (medplum: MedplumClient) => {
  const res = await medplum.searchResources(
    "Organization",
    "type=Healthcare Provider&_count=100"
  );
  const providerData = res.map((provider: any) => {
    const npi = provider.identifier
      ? provider.identifier.find(
          (id: any) => id.system == codingSystemMap["provider"]
        )
      : null;
    // console.log(npi?.value + " " + provider.name)
    return {
      value: provider.id,
      label: provider.name, //provider.name,
      npi: npi?.value,
    };
  });
  return providerData;
};
const getCoverageResponse = async (medplum: MedplumClient, id: string) => {
  const coverageRes = (await medplum.readResource(
    "CoverageEligibilityResponse",
    id
  )) as any;
  const coverage = (await medplum.readReference(
    coverageRes.insurance[0].coverage
  )) as any;
  const payor = await medplum.readReference(coverageRes.insurer);
  const patientDetails = await medplum.readReference(coverageRes.patient);
  const related = coverage?.subscriber
    ? await medplum.readReference(coverage.subscriber)
    : null;
  const planSummary = JSON.parse(coverageRes?.extension[0]?.valueString);

  return {
    ...coverageRes,
    coverage: { ...coverage },
    insurer: { ...payor },
    patientDetails: { ...patientDetails },
    related: related,
    planSummary: planSummary,
  };
};

function getPatientName(patientData: any) {
  let name = "--";
  if (patientData?.length > 0) {
    patientData.forEach((res: any) => {
      if ((res?.use === "official" || res?.use === "usual") && name === "--") {
        name = res?.text || res?.given.join(" ") + " " + res.family;
      }
    });
    if (name === "--")
      name = patientData[0]?.text || patientData[0]?.given || "--";
    if (typeof name !== "string") {
      name = String(name);
    }
  }
  return name;
}

const formatAddress = (add: any) => {
  if (!add) return "--";
  let tempArr = [];
  if (add?.city) {
    tempArr.push(add.city);
  }
  if (add?.state) {
    tempArr.push(add.state);
  }
  if (add?.postalCode) {
    tempArr.push(add.postalCode);
  }
  if (add?.country) {
    tempArr.push(add.country);
  }
  return tempArr.join(", ");
};

function splitCamelCase(str: string) {
  return str.split(/(?=[A-Z])/).join(" ");
}

const generateHPBCSummary = (summList: any) => {
  const tileDataArray: any = [];
  const hbpc = JSON.parse(
    summList.find((sry: any) => sry?.url == "HBPC_Deductible_OOP_Summary")
      .valueString
  );
  const hbpcSumm = [
    {
      title: "Family Deductible",
      colName: ["Annual", "Remaining"],
      inNetworkCoINS: hbpc?.FamilyDeductibleInNet?.Value ?? "--",
      inNetworkCopay: hbpc?.FamilyDeductibleRemainingInNet?.Value ?? "--",
      outNetworkCoINS: hbpc?.FamilyDeductibleOutNet?.Value ?? "--",
      outNetworkCopay: hbpc?.FamilyDeductibleRemainingOutNet?.Value ?? "--",
    },
    {
      title: "Individual Deductible",
      colName: ["Annual", "Remaining"],
      inNetworkCoINS: hbpc?.IndividualDeductibleInNet?.Value ?? "--",
      inNetworkCopay: hbpc?.IndividualDeductibleRemainingInNet?.Value ?? "--",
      outNetworkCoINS: hbpc?.IndividualDeductibleOutNet?.Value ?? "--",
      outNetworkCopay: hbpc?.IndividualDeductibleRemainingOutNet?.Value ?? "--",
    },
    {
      title: "Family Out-of-Pocket",
      colName: ["Maximum", "Remaining"],
      inNetworkCoINS: hbpc?.FamilyOOPInNet?.Value ?? "--",
      inNetworkCopay: hbpc?.FamilyOOPRemainingInNet?.Value ?? "--",
      outNetworkCoINS: hbpc?.FamilyOOPOutNet?.Value ?? "--",
      outNetworkCopay: hbpc?.FamilyOOPRemainingOutNet?.Value ?? "--",
    },
    {
      title: "Individual Out-of-Pocket",
      colName: ["Maximum", "Remaining"],
      inNetworkCoINS: hbpc?.IndividualOOPInNet?.Value ?? "--",
      inNetworkCopay: hbpc?.IndividualOOPRemainingInNet?.Value ?? "--",
      outNetworkCoINS: hbpc?.IndividualOOPOutNet?.Value ?? "--",
      outNetworkCopay: hbpc?.IndividualOOPRemainingOutNet?.Value ?? "--",
    },
  ];

  const servicesSummary = summList?.reduce((acc: any, curr: any) => {
    if (
      curr?.url != "HBPC_Deductible_OOP_Summary" &&
      curr?.url != "PlanCoverageSummary"
    ) {
      const summJSON = JSON.parse(curr?.valueString);

      const summary = [
        {
          title: splitCamelCase(curr?.url),
          colName: ["Co-Pay", "Co-INS"],
          inNetworkCoINS: summJSON?.CoInsInNet?.Value ?? "--",
          inNetworkCopay: summJSON?.CoPayInNet?.Value ?? "--",
          outNetworkCoINS: summJSON?.CoInsOutNet?.Value ?? "--",
          outNetworkCopay: summJSON?.CoPayOutNet?.Value ?? "--",
        },
      ];
      if (summJSON?.TherapyVisitsLimitationsInNet)
        summary.push({
          title:
            splitCamelCase(curr?.url).replace("Summary", "Visit ") +
            "Limitations",
          colName: ["Limitations", "Remaining"],
          inNetworkCoINS:
            summJSON?.TherapyVisitsLimitationsInNet?.Value ?? "--",
          inNetworkCopay: summJSON?.TherapyVisitsRemainingInNet?.Value ?? "--",
          outNetworkCoINS:
            summJSON?.TherapyVisitsLimitationsOutNet?.Value ?? "--",
          outNetworkCopay:
            summJSON?.TherapyVisitsRemainingOutNet?.Value ?? "--",
        });
      acc.push(...summary);
    }
    return acc;
  }, []);

  tileDataArray.push(...hbpcSumm);
  tileDataArray.push(...servicesSummary);
  return tileDataArray;
};
const MedicalSummary = (summList: any) => {
  if (!summList.find((sry: any) => sry?.url == "MedicareInfoSummary")) {
    return;
  }
  const tileDataArray: any = [];

  const medInfo = JSON.parse(
    summList.find((sry: any) => sry?.url == "MedicareInfoSummary")?.valueString
  );

  const Advantage = {
    "Advantage Payer Name": medInfo.AdvantagePayerName ?? "--",
    " Advantage Type": medInfo.AdvantagePolicyType ?? "--",
    " Advantage COB Start Date": medInfo.AdvantageCOBStartDate ?? "--",
    " Advantage COB End Date": medInfo.AdvantageCOBEndDate ?? "--",
  };
  const Hospic = {
    "Hospic Qunantity": medInfo.HospiceQty ?? "--",
    "Hospic Start Date": medInfo.HospiceStartDate ?? "--",
    "Hospic End Date": medInfo.HospiceEndDate ?? "--",
  };
  const homeHealth = {
    "Home Health Care Start Date": medInfo.HomeHealthCareStartDate ?? "--",
    "Home Health Care End Date": medInfo.HomeHealthCareEndDate ?? "--",
  };
  const Pharmacy = {
    " Pharmacy Payer Name": medInfo.PharmacyPayerName ?? "--",
    " Pharmacy Network Id": medInfo.PharmacyPayerPlanNetworkID ?? "--",
    "Pharmacy Plan Number": medInfo.PharmacyPayerPlanNumber ?? "--",
  };
  const Nursing = {
    "Nursing Care Start Date": medInfo.SkilledNursingCareStartDate ?? "--",
    "Nursing Care End Date": medInfo.SkilledNursingCareEndDate ?? "--",
  };
  const liability = {
    "WC Auto Injury Liability Payer Name":
      medInfo.WCAutoInjuryLiabilityPayerName ?? "--",
    "WC Auto Injury Liability Policy Type":
      medInfo.WCAutoInjuryLiabilityPolicyType ?? "--",
    " WC Auto Injury Liability Start Date":
      medInfo.WCAutoInjuryLiabilityCOBStartDate ?? "--",
    "WC Auto Injury Liability End Date":
      medInfo.WCAutoInjuryLiabilityCOBEndDate ?? "--",
  };
  const QMBPolicy = {
    "QMB Policy Type": medInfo.QMBPolicyType ?? "--",
    BIN: medInfo.BIN ?? "--",
    PCN: medInfo.PCN ?? "--",
  };

  const dataSections = [
    { title: "Advantage", data: Advantage },
    { title: "Hospic", data: Hospic },
    { title: "Home Health", data: homeHealth },
    { title: "Pharmacy", data: Pharmacy },
    { title: "Nursing", data: Nursing },
    { title: "Liability", data: liability },
    { title: "QMB Policy", data: QMBPolicy },
  ];
  return { ...medInfo, dataSections };
};

const mapServiceDetails = (serviceDetails: any) => {
  let serviceDetailsMapArray = [];

  for (const service of serviceDetails) {
    if (service?.item?.length === 0) {
      continue;
    }

    const sectionFirstMapArray = service?.item?.reduce(
      (acc: any, curr: any) => {
        if (!curr?.network?.coding[0].code) {
          const item = {
            title: `${curr?.unit?.coding?.[0]?.display??''} ${curr?.category?.text??''}`,
            details: curr?.description?.split("\n"),
          };
          acc.push(item);
        }
        return acc;
      },
      []
    );

    const netSectionMap = service?.item?.reduce(
      (acc: any, curr: any) => {
        if (curr?.network?.coding[0].code) {
          const item = {
            title: `${curr?.unit?.coding?.[0]?.display??''} ${curr?.category?.text??''}`,
            sideString:
              curr?.benefit?.[0]?.allowedString ??
              curr?.benefit?.[0]?.usedString,
            details: curr?.description?.split("\n"),
          };
          if (curr?.network?.coding?.[0].code == "yes") acc.inPlan.push(item);
          else acc.outPlan.push(item);
        }
        return acc;
      },
      { inPlan: [], outPlan: [] }
    );
    const serviceDetailsMap = {
      name: service?.item?.[0]?.name,
      firstSection: sectionFirstMapArray,
      inNetSection: netSectionMap?.inPlan ?? [],
      outNetSection: netSectionMap?.outPlan ?? [],
    };

    serviceDetailsMapArray.push(serviceDetailsMap);
  }
  serviceDetailsMapArray.filter((item: any) => item.name);
  return serviceDetailsMapArray;
};

const submitFormData = async (
  medplum: MedplumClient,
  form: any,
  verificationType: string,
  service: 'pverify' | 'availity'
) => {
  // create coverage
  const patientId =
    verificationType == "Self" ? form.subscriber.id : form.dependent.id;
  const coverage: any = await medplum.createResource({
    resourceType: "Coverage",
    status: "active",
    type: {
      coding: [
        {
          system: codingSystemMap["benefitCategory"],
          code: form.benefitCategory,
        },
      ],
    },
    policyHolder: {
      reference: `Organization/${form.payer.id}`,
    },
    subscriber:
      verificationType == "Dependent"
        ? {
            reference: `RelatedPerson/${form.subscriber.id}`,
          }
        : undefined,
    subscriberId: form.subscriber.memberId,
    beneficiary: {
      reference: `Patient/${patientId}`,
    },
    payor: [
      {
        reference: `Organization/${form.payer.id}`,
      },
    ],
  });

  const coverageEligibilityRequest: any = await medplum.createResource({
    resourceType: "CoverageEligibilityRequest",
    status: "active",
    purpose: ["validation"],
    patient: {
      reference: `Patient/${patientId}`,
    },
    servicedPeriod: {
      start: form.miscellaneous.fromDate.toISOString().split("T")[0],
      end: form.miscellaneous.toDate.toISOString().split("T")[0],
    },
    created: new Date().toISOString().split("T")[0],
    insurer: {
      reference: `Organization/${form.payer.id}`,
    },
    insurance: [
      {
        coverage: {
          reference: `Coverage/${coverage.id}`,
        },
      },
    ],
    provider: {
      reference: `Organization/${form.provider.id}`,
      display: form.provider?.name,

    },
    item: [
      {
        category: {
          coding: [
            {
              code: form.serviceType.id,
              display: serviceTypeMap(service).find(
                (x: any) => x.value == form.serviceType.id
              )?.label,
            },
          ],
        },
      },
    ],
  });
  try {
    const res = await medplum.executeBot(
      {
        system: "https://api-stage.healthconnect.systems/bots",
        value: `${service}-service`,
      },
      { coverageRequestId: coverageEligibilityRequest.id, request: 'getCoverageEligibilityResponse'}
    );
    return res;
  } catch (err) {
    console.error("Unexpected error:", err);
  }
};
const patientSearchList = async (patientName: string, medplum: any) => {
  let queryString = `?name=${patientName}&_count=10`;
  const response = await medplum.searchResources("Patient", queryString);
  return response;
};

const CoverageSearchList = async (id: any, medplum: any, pageNumber: any, serviceType: 'pverify' | 'availity') => {
  const count = 20;
  let queryString = `?_count=20&_offset=${
    (pageNumber - 1) * count
  }&_sort=-_lastUpdated&_total=accurate&patient=Patient/${id}&identifier=${codingSystemMap[serviceType]}|`;
  const response = await medplum.searchResources(
    "CoverageEligibilityResponse",
    queryString
  );

  return response;
};

export {
  getPayerList,
  getPatientList,
  getProviderList,
  getRelatedPersonList,
  getPatientName,
  updatePatient,
  updateProvider,
  submitFormData,
  getCoverageResponse,
  codingSystemMap,
  formatAddress,
  mapServiceDetails,
  generateHPBCSummary,
  patientSearchList,
  CoverageSearchList,
  MedicalSummary,
  
};
